type DataPoint = { label: string; [key: string]: string | number }

const MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

const formatDayLabel = (date: Date): string => {
  return `${MONTHS_SHORT[date.getMonth()]} ${String(date.getDate()).padStart(2, '0')}`
}

const formatMonthLabel = (date: Date): string => {
  return `${MONTHS_SHORT[date.getMonth()]} ${date.getFullYear()}`
}

const getISOWeekNumber = (date: Date): number => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7))
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7)
}

const getPeriodDays = (period: string): number | null => {
  switch (period) {
    case 'last_7_days':
      return 7
    case 'last_30_days':
      return 30
    default:
      return null
  }
}

const generateDayLabels = (days: number, today: Date): string[] => {
  const labels: string[] = []
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(today)
    date.setDate(date.getDate() - i)
    labels.push(formatDayLabel(date))
  }
  return labels
}

const generateWeekLabels = (days: number, today: Date): string[] => {
  const labels: string[] = []
  const seen = new Set<string>()
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(today)
    date.setDate(date.getDate() - i)
    const weekLabel = `Week ${String(getISOWeekNumber(date)).padStart(2, '0')}`
    if (!seen.has(weekLabel)) {
      seen.add(weekLabel)
      labels.push(weekLabel)
    }
  }
  return labels
}

const generateMonthLabels = (months: number, today: Date): string[] => {
  const labels: string[] = []
  for (let i = months - 1; i >= 0; i--) {
    const date = new Date(today.getFullYear(), today.getMonth() - i, 1)
    labels.push(formatMonthLabel(date))
  }
  return labels
}

export const padTimeSeries = <T extends DataPoint>(
  data: T[],
  period: string,
  defaults: Omit<T, 'label'>,
  today: Date = new Date()
): T[] => {
  if (period === 'all_time') return data

  const isMonthly = period === 'last_12_months'
  const isWeekly = period === 'last_90_days'
  const labels = isMonthly
    ? generateMonthLabels(12, today)
    : isWeekly
      ? generateWeekLabels(90, today)
      : generateDayLabels(getPeriodDays(period) ?? 30, today)

  const dataByLabel = new Map(data.map((point) => [point.label, point]))

  return labels.map((label) => {
    const existing = dataByLabel.get(label)
    if (existing) return existing
    // Safe: spreading all T keys from defaults + overriding label produces a valid T
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    return { ...defaults, label } satisfies DataPoint as T
  })
}
